import { Alert, Anchor, Button, Card, Grid, Group, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconExternalLink } from '@tabler/icons-react';
import countries from 'country-list';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { regulatoryCountriesEU } from '../../constants/FacebookAd';
import isInvalidCronExpression from '../../helpers/cronValidator';
import useFetch from '../../helpers/useFetch';
import CountrySelect from '../CountrySelect';
import FacebookCatalogUploadStatus from './FacebookCatalogUploadStatus';
import FacebookRemoveStaleCatalogItemsButton from './FacebookRemoveStaleCatalogItemsButton';
import NextSyncLabel from '../NextSyncLabel';
import OptionsSelect from '../OptionsSelect';
import SuperAdminArea from '../SuperAdmin/SuperAdminArea';
import SuperAdminBadge from '../SuperAdmin/SuperAdminBadge';

let regulatoryCountriesEUCodes = [];
regulatoryCountriesEU.forEach((c) => {
  regulatoryCountriesEUCodes.push(countries.getCode(c));
});

const FacebookGeneralSettings = ({
  facebookAccount,
  handleChange,
  terms,
  termsLoading,
  syncProductsNow,
  syncAudiencesNow,
  isSuperAdmin,
  onFacebookAccountChange,
}) => {
  const regulatoryMatch = facebookAccount?.countries?.some((country) => regulatoryCountriesEUCodes.includes(country));

  const handleRegulatoryChange = (e) => {
    const valueId = e.target.value;
    const sectionTitle = e.target.name;

    if (sectionTitle === 'adPayerId') {
      handleChange({ target: { name: 'adPayer', value: valueId } });
    } else if (sectionTitle === 'adBeneficiaryId') {
      handleChange({ target: { name: 'adBeneficiary', value: valueId } });
    }
  };

  const handleCountryChange = (value) => {
    handleChange({ target: { name: 'countries', value } });
  };

  // useFetch rejects with the parsed JSON body (a plain object) for
  // non-2xx responses, not an Error instance — `String(err)` would
  // render "[object Object]". Walk the likely body shapes and force a
  // string output even if a candidate is itself an object (e.g. the
  // nested `{ error: { code, message } }` shape Meta sometimes carries
  // through to our API responses). Without the typeof-string guard,
  // setting an object value into `externalCatalogsError` would throw
  // "Objects are not valid as a React child" when the JSX renders it.
  const asErrorMessage = (err, fallback) => {
    const candidates = [err?.message, err?.error?.message, err?.error, err?.body?.message, err?.body];
    for (const c of candidates) {
      if (typeof c === 'string' && c.length > 0) return c;
    }
    return fallback;
  };

  const { accountId, domainId } = useParams();
  const fetchAdmin = useFetch();
  const [externalCatalogs, setExternalCatalogs] = useState(null);
  const [externalCatalogsError, setExternalCatalogsError] = useState(null);
  const [validatingExternalCatalog, setValidatingExternalCatalog] = useState(false);

  // Load the Business's owned + client catalogs when external-catalog mode
  // is on. Skipped unless a Business is linked (the API gates on this too,
  // returning 400 — surface that as the empty state instead of a noisy
  // error toast). Re-runs when the operator toggles the flag back on so
  // they can re-pick a catalog after a clear-on-toggle.
  useEffect(() => {
    if (!isSuperAdmin || !facebookAccount?.businessId || !facebookAccount?.useExternalCatalog) {
      setExternalCatalogs(null);
      setExternalCatalogsError(null);
      return;
    }
    let cancelled = false;
    setExternalCatalogsError(null);
    fetchAdmin(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/catalogs`)
      .then((res) => {
        if (!cancelled) setExternalCatalogs(res || { owned: [], client: [] });
      })
      .catch((err) => {
        if (!cancelled) {
          setExternalCatalogsError(asErrorMessage(err, 'Failed to load catalogs'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, domainId, fetchAdmin, facebookAccount?.businessId, facebookAccount?.useExternalCatalog, isSuperAdmin]);

  // Two-step state update so the catalog dropdown blanks the moment the
  // toggle flips, matching the API's clear-on-toggle behaviour. Without
  // this, the UI would show a stale catalogId post-toggle until the
  // operator saved and reloaded.
  const handleExternalCatalogToggle = (checked) => {
    handleChange({ target: { name: 'useExternalCatalog', value: checked } });
    handleChange({ target: { name: 'catalogId', value: '' } });
  };

  const handleExternalCatalogChange = (value) => {
    handleChange({ target: { name: 'catalogId', value: value || '' } });
  };

  const runSanityCheck = async () => {
    if (!facebookAccount?.catalogId) return;
    setValidatingExternalCatalog(true);
    try {
      const result = await fetchAdmin(
        `/api/admin/accounts/${accountId}/domains/${domainId}/facebook/catalog-sanity-check`,
        { method: 'POST', body: JSON.stringify({ catalogId: facebookAccount.catalogId }) }
      );
      // Three-bucket coloring: fully matched (success), partial (warn — the
      // catalog probably is the right one but some sampled groups didn't
      // resolve in ES — could be staleness either way), nothing matched
      // (error — almost certainly the wrong catalog or wrong useSkuAsItemGroupId
      // setting). We surface the first few mismatches so the operator has a
      // concrete value to paste into Shopify or to check in ES.
      const sampled = result?.sampled ?? 0;
      const matched = result?.matched ?? 0;
      const tone = sampled === 0 ? 'warning' : matched === sampled ? 'success' : matched > 0 ? 'warning' : 'danger';
      const mismatchHint = result?.mismatches?.length
        ? ` Unmatched samples: ${result.mismatches.slice(0, 3).join(', ')}.`
        : '';
      const fieldHint = result?.matchedField ? ` (matched on ${result.matchedField})` : '';
      notifications.show({
        title:
          sampled === 0
            ? 'Catalog returned no item_group_id values to sample'
            : `Matched ${matched}/${sampled}${fieldHint}`,
        message:
          sampled === 0
            ? `The selected catalog contains ${result?.totalCatalogItems ?? 'an unknown number of'} items but no item_group_id was set on any of them.`
            : `Releva will only resolve product-set membership for items whose item_group_id is in our index.${mismatchHint}`,
        color: tone,
      });
    } catch (err) {
      notifications.show({
        title: 'Catalog validation failed',
        message: asErrorMessage(err, 'Unknown error'),
        color: 'danger',
      });
    } finally {
      setValidatingExternalCatalog(false);
    }
  };

  // Build the dropdown options. A catalog could appear in both
  // `owned` and `client` (the customer's Business owns it AND it's
  // shared back to them — rare, but possible). Mantine Select misbehaves
  // with duplicate values, so dedupe by id and prefer the `owned`
  // record. Owned is also the more authoritative label for the picker:
  // the operator generally wants to know "is this the Business's own
  // catalog or a shared one" and "owned" is the stronger signal.
  // Defensive String() coercion on every id: Mantine Select compares
  // option values as strings, so if the API ever returned a numeric
  // catalog id the saved-vs-option equality check below would silently
  // miss the selected entry.
  const dedupedCatalogs = (() => {
    if (!externalCatalogs) return [];
    const byId = new Map();
    for (const c of externalCatalogs.client || []) byId.set(String(c.id), { ...c, id: String(c.id), source: 'client' });
    for (const c of externalCatalogs.owned || []) byId.set(String(c.id), { ...c, id: String(c.id), source: 'owned' });
    return Array.from(byId.values());
  })();

  const visibleCatalogOptions = dedupedCatalogs.map((c) => ({
    value: c.id,
    label: `[${c.source}] ${c.name}${c.productCount != null ? ` (${c.productCount} items)` : ''}`,
  }));

  // Three states the Select needs to differentiate:
  //   * loading (fetch in flight)           → disabled + "Loading…" placeholder
  //   * loaded with at least one option     → enabled
  //   * loaded with an error OR empty list  → enabled IF a saved id is
  //     present (so the operator can SEE / CLEAR it from this control —
  //     the escape-hatch the previous fix added). When there's no saved
  //     id, an enabled-but-empty Select is fine — the placeholder text
  //     and the error/info Texts below explain what's going on.
  //
  // `catalogsLoadDone` is success OR explicit failure — anything except
  // "still in flight." Keying disabled and placeholder on this avoids
  // the bug where a failed fetch left externalCatalogs=null and the
  // Select read "Loading…" indefinitely with no escape.
  const catalogsLoadDone = !!externalCatalogs || !!externalCatalogsError;
  const savedCatalogId = facebookAccount?.catalogId ? String(facebookAccount.catalogId) : null;
  const savedInVisibleList = !!savedCatalogId && visibleCatalogOptions.some((o) => o.value === savedCatalogId);
  // Render a synthetic option for the saved id when it isn't represented
  // in the fetched list — covers two cases:
  //   * fetch succeeded but the saved catalog isn't visible to the
  //     Business (deleted, unshared, permission revoked).
  //   * fetch errored entirely; we have no visible list to consult.
  // In both cases the operator needs to be able to SEE the saved value
  // and CLEAR it from this control without escaping via toggle off/on.
  const renderSyntheticSavedOption = !!savedCatalogId && !savedInVisibleList && catalogsLoadDone;
  const externalCatalogOptions = renderSyntheticSavedOption
    ? [
        {
          value: savedCatalogId,
          label: externalCatalogsError
            ? `(saved id — catalog list failed to load) ${savedCatalogId}`
            : `(saved id — not visible to this Business) ${savedCatalogId}`,
        },
        ...visibleCatalogOptions,
      ]
    : visibleCatalogOptions;

  const catalogSelectPlaceholder = !catalogsLoadDone
    ? 'Loading…'
    : externalCatalogsError
      ? 'Catalogs failed to load'
      : 'Pick a catalog';

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Stack gap="md">
          <Grid>
            <Grid.Col span={4}>
              <Text size="sm" fw={500}>
                Name
              </Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <TextInput disabled name="name" type="text" value={facebookAccount.name} />
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={4}>
              <Text size="sm" fw={500}>
                Facebook id
              </Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <TextInput disabled name="facebookId" type="text" value={facebookAccount.facebookId} />
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={4}>
              <Text size="sm" fw={500}>
                Business
              </Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <OptionsSelect
                name="business"
                endpoint="facebook/businesses"
                handleChange={handleChange}
                text="The business that is selected above will receive the 'Releva Products Catalog'
                in Facebook and it will be automatically updated on product changes."
                value={facebookAccount?.businessId || ''}
              />
            </Grid.Col>
          </Grid>

          {facebookAccount?.businessId && (
            <>
              {isSuperAdmin && (
                <Grid>
                  <Grid.Col span={4}>
                    <Group gap="xs">
                      <Text fw={500}>Facebook enabled</Text>
                      <SuperAdminBadge show />
                    </Group>
                  </Grid.Col>
                  <Grid.Col span={8}>
                    <Switch
                      checked={facebookAccount?.facebookEnabled ?? false}
                      onChange={(e) =>
                        handleChange({ target: { name: 'facebookEnabled', value: e.currentTarget.checked } })
                      }
                    />
                    <Text size="sm" c="dimmed" mt="xs">
                      Master switch for the Facebook integration. Disabling it stops all outbound Facebook operations
                      (catalog sync, product sets, audiences, ads and server-side CAPI tracking) and preserves every
                      setting — nothing is reset, and re-enabling resumes the same configuration.
                    </Text>
                    <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" mt="xs">
                      Disabling does not stop ad spend. Ads already live in Meta keep delivering until their configured
                      duration ends. To halt spend, stop the campaigns that run them.
                    </Alert>
                  </Grid.Col>
                </Grid>
              )}

              <Grid>
                <Grid.Col span={4}>
                  <Text fw={500}>Ad account</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  {facebookAccount?.adAccountId && !termsLoading && !terms.accepted && (
                    <Text size="sm" c="red" mb="xs">
                      You need to accept Facebook Custom Audience Terms of Service{' '}
                      <Anchor
                        href={`https://business.facebook.com/ads/manage/customaudiences/tos/?act=${facebookAccount.adAccountId.replace(
                          'act_',
                          ''
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconExternalLink size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> in the
                        Business Manager
                      </Anchor>
                      .
                    </Text>
                  )}
                  {facebookAccount?.adAccountId && !termsLoading && !terms.valueBasedAccepted && (
                    <Text size="sm" c="red" mb="xs">
                      You need to accept Facebook Value Based Custom Audience Terms of Service{' '}
                      <Anchor
                        href={`https://www.facebook.com/customaudiences/value_based/tos/?act=${facebookAccount.adAccountId.replace(
                          'act_',
                          ''
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconExternalLink size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> in the
                        Business Manager
                      </Anchor>
                      .
                    </Text>
                  )}

                  <OptionsSelect
                    name="adAccount"
                    endpoint={`facebook/adAccounts/${facebookAccount.businessId}`}
                    handleChange={handleChange}
                    text="Facebook audiences will be associated with the selected Ad account."
                    value={facebookAccount?.adAccountId || ''}
                  />
                </Grid.Col>
              </Grid>

              <Grid>
                <Grid.Col span={4}>
                  <Text fw={500}>Page</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <OptionsSelect
                    name="page"
                    endpoint="facebook/pages"
                    handleChange={handleChange}
                    text="The page that is used for publishing facebook ads."
                    value={facebookAccount?.pageId || ''}
                  />
                </Grid.Col>
              </Grid>

              {facebookAccount.adAccountId && (
                <>
                  <Grid>
                    <Grid.Col span={4}>
                      <Text fw={500}>Pixel</Text>
                    </Grid.Col>
                    <Grid.Col span={8}>
                      <OptionsSelect
                        name="pixel"
                        endpoint={`facebook/pixels/${facebookAccount.adAccountId}`}
                        handleChange={handleChange}
                        text="Select pixel to use for running ads and sending backend events (if enabled)."
                        value={facebookAccount?.pixelId || ''}
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={4}>
                      <Text fw={500}>Instagram Business Account</Text>
                    </Grid.Col>
                    <Grid.Col span={8}>
                      <OptionsSelect
                        name="instagramAccount"
                        endpoint={`facebook/accounts/${facebookAccount.adAccountId}`}
                        handleChange={handleChange}
                        text="The Instagram Business Account associated with your page."
                        value={facebookAccount?.instagramAccountId || ''}
                        required={false}
                      />
                    </Grid.Col>
                  </Grid>
                </>
              )}

              {isSuperAdmin && (
                <Grid>
                  <Grid.Col span={4}>
                    <Group gap="xs">
                      <Text fw={500}>Advantage+ Creative</Text>
                      <SuperAdminBadge show />
                    </Group>
                  </Grid.Col>
                  <Grid.Col span={8}>
                    <Switch
                      checked={facebookAccount?.advantagePlusCreativeEnabled ?? true}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: 'advantagePlusCreativeEnabled',
                            value: e.currentTarget.checked,
                          },
                        })
                      }
                    />

                    <Text size="sm" c="dimmed" mt="xs">
                      Apply Meta Advantage+ Creative enhancements (image uncrop, placement adapt) when creating ads.
                      Disable if the ad account is not enrolled in Advantage+ — otherwise Meta rejects ad creation with
                      error 100/3858023.
                    </Text>
                  </Grid.Col>
                </Grid>
              )}

              {isSuperAdmin && (
                <SuperAdminArea show>
                  <Grid>
                    <Grid.Col span={4}>
                      <Text fw={500}>External catalog mode</Text>
                    </Grid.Col>
                    <Grid.Col span={8}>
                      <Switch
                        checked={!!facebookAccount?.useExternalCatalog}
                        onChange={(e) => handleExternalCatalogToggle(e.currentTarget.checked)}
                        aria-label="External catalog mode"
                      />
                      <Text size="sm" c="dimmed" mt="xs">
                        When enabled, Releva treats <code>catalogId</code> as an externally-managed catalog (typically
                        the one Shopify&apos;s official Facebook &amp; Instagram app maintains) and skips its own
                        catalog sync and delete jobs. Product-set membership is still re-evaluated against the external
                        catalog on the normal cadence. Toggling clears the saved catalog — pick a new one below before
                        saving.
                      </Text>
                      {facebookAccount?.useExternalCatalog && (
                        <Stack gap="xs" mt="md">
                          {externalCatalogsError && (
                            <Text size="sm" c="red">
                              {externalCatalogsError}
                            </Text>
                          )}
                          <Select
                            label="External catalog"
                            placeholder={catalogSelectPlaceholder}
                            // Stay enabled once the fetch settles (success
                            // OR error) so the operator can interact with
                            // any saved id surfaced via the synthetic
                            // option, even on an errored fetch. Disabled
                            // only during the in-flight window.
                            disabled={!catalogsLoadDone}
                            data={externalCatalogOptions}
                            value={savedCatalogId}
                            onChange={handleExternalCatalogChange}
                            searchable
                            clearable
                            nothingFoundMessage="No catalogs visible to this Business"
                          />
                          {renderSyntheticSavedOption && (
                            <Text size="sm" c="orange">
                              {externalCatalogsError ? (
                                <>
                                  Couldn&apos;t fetch the catalog list, so we can&apos;t confirm whether the saved id{' '}
                                  <code>{savedCatalogId}</code> is still visible to this Business. Retry, or clear the
                                  saved id if you suspect it&apos;s stale.
                                </>
                              ) : (
                                <>
                                  The saved catalog id <code>{savedCatalogId}</code> isn&apos;t in the list returned by
                                  this Business. The catalog may have been deleted, unshared, or its permissions
                                  revoked. Pick a visible catalog or clear this one.
                                </>
                              )}
                            </Text>
                          )}
                          <Group>
                            <Button
                              disabled={!facebookAccount?.catalogId || validatingExternalCatalog}
                              loading={validatingExternalCatalog}
                              onClick={runSanityCheck}
                              variant="light"
                            >
                              Validate catalog against Releva products
                            </Button>
                          </Group>
                        </Stack>
                      )}
                    </Grid.Col>
                  </Grid>
                </SuperAdminArea>
              )}

              {isSuperAdmin && (
                <Grid>
                  <Grid.Col span={4}>
                    <Group gap="xs">
                      <Text fw={500}>Use SKU as item_group_id</Text>
                      <SuperAdminBadge show />
                    </Group>
                  </Grid.Col>
                  <Grid.Col span={8}>
                    <Switch
                      checked={facebookAccount?.useSkuAsItemGroupId ?? false}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: 'useSkuAsItemGroupId',
                            value: e.currentTarget.checked,
                          },
                        })
                      }
                    />

                    <Text size="sm" c="dimmed" mt="xs">
                      Emits <code>item_group_id = doc.sku</code> in the Meta catalog instead of the default group id.
                      Use when the advertiser's Pixel / CAPI events fire with{' '}
                      <code>content_type=&#39;product_group&#39;</code> and <code>content_ids=[SKU]</code> — Meta
                      resolves that against <code>item_group_id</code>, so the catalog must carry the SKU there for
                      event-to-catalog matching. Orthogonal to the variant-aware toggle above. Falls back to the default
                      per-mode <code>item_group_id</code> for docs with no SKU set.
                    </Text>
                  </Grid.Col>
                </Grid>
              )}

              <Grid>
                <Grid.Col span={4}>
                  <Text fw={500}>Catalog Sync Schedule</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <Group gap="xs">
                    <TextInput
                      name="productSyncSchedule"
                      placeholder="0 7 * * *"
                      required
                      value={facebookAccount?.productSyncSchedule || ''}
                      error={
                        isInvalidCronExpression(facebookAccount?.productSyncSchedule) ? 'Invalid cron expression' : null
                      }
                      onChange={handleChange}
                      style={{ flex: 1 }}
                    />
                    <Button disabled={!facebookAccount?.productSyncSchedule} onClick={() => syncProductsNow()}>
                      Sync Now
                    </Button>
                  </Group>
                  <Text size="sm" c="dimmed" mt="xs">
                    This{' '}
                    <Anchor href="https://crontab.guru/" rel="noreferrer" target="_blank">
                      cron expression{' '}
                      <IconExternalLink size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />
                    </Anchor>{' '}
                    controls how often we sync the product catalog from Releva to Facebook. It will be evaluated under
                    the UTC timezone.
                  </Text>
                  <NextSyncLabel schedule={facebookAccount?.productSyncSchedule} />
                  {!facebookAccount?.useExternalCatalog && (
                    <FacebookCatalogUploadStatus facebookAccount={facebookAccount} isSuperAdmin={isSuperAdmin} />
                  )}
                  {isSuperAdmin && !facebookAccount?.useExternalCatalog && (
                    <SuperAdminArea show mt="md">
                      <FacebookRemoveStaleCatalogItemsButton
                        facebookAccount={facebookAccount}
                        onFacebookAccountChange={onFacebookAccountChange}
                      />
                    </SuperAdminArea>
                  )}
                </Grid.Col>
              </Grid>

              <Grid>
                <Grid.Col span={4}>
                  <Text fw={500}>Audience Sync Schedule</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <Group gap="xs">
                    <TextInput
                      name="audienceSyncSchedule"
                      placeholder="0 0 * * *"
                      required
                      value={facebookAccount?.audienceSyncSchedule || ''}
                      error={
                        isInvalidCronExpression(facebookAccount?.audienceSyncSchedule)
                          ? 'Invalid cron expression'
                          : null
                      }
                      onChange={handleChange}
                      style={{ flex: 1 }}
                    />
                    <Button disabled={!facebookAccount?.audienceSyncSchedule} onClick={() => syncAudiencesNow()}>
                      Sync Now
                    </Button>
                  </Group>
                  <Text size="sm" c="dimmed" mt="xs">
                    This{' '}
                    <Anchor href="https://crontab.guru/" rel="noreferrer" target="_blank">
                      cron expression{' '}
                      <IconExternalLink size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />
                    </Anchor>{' '}
                    controls how often we sync facebook audiences from "Sync Segments". It will be evaluated under the
                    UTC timezone.
                  </Text>
                  <NextSyncLabel schedule={facebookAccount?.audienceSyncSchedule} />
                </Grid.Col>
              </Grid>

              <Grid>
                <Grid.Col span={4}>
                  <Text fw={500}>Targeting countries</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <CountrySelect onChange={handleCountryChange} selected={facebookAccount?.countries} />
                  <Text size="sm" c="dimmed" mt="xs">
                    List of countries where your facebook ads will be delivered.
                  </Text>
                </Grid.Col>
              </Grid>

              {regulatoryMatch && (
                <Grid>
                  <Grid.Col span={4}>
                    <Text fw={500}>Beneficiary</Text>
                  </Grid.Col>
                  <Grid.Col span={8}>
                    <OptionsSelect
                      name="adBeneficiary"
                      endpoint="facebook/businesses"
                      handleChange={handleRegulatoryChange}
                      text={
                        <>
                          Targeting countries from the European Union requires selecting a beneficiary for your Facebook
                          ads.{' '}
                          <Anchor
                            href="https://www.facebook.com/business/help/605021638170961/"
                            target="_blank"
                            rel="noreferrer"
                          >
                            <strong>Learn more.</strong>
                          </Anchor>
                        </>
                      }
                      value={facebookAccount?.adBeneficiary || ''}
                    />
                  </Grid.Col>
                </Grid>
              )}
              {regulatoryMatch && (
                <Grid>
                  <Grid.Col span={4}>
                    <Text fw={500}>Payer</Text>
                  </Grid.Col>
                  <Grid.Col span={8}>
                    <OptionsSelect
                      name="adPayer"
                      endpoint="facebook/businesses"
                      handleChange={handleRegulatoryChange}
                      text={
                        <>
                          Targeting countries from the European Union requires selecting a payer for your Facebook ads.{' '}
                          <Anchor
                            href="https://www.facebook.com/business/help/605021638170961/"
                            target="_blank"
                            rel="noreferrer"
                          >
                            <strong>Learn more.</strong>
                          </Anchor>
                        </>
                      }
                      value={facebookAccount?.adPayer || ''}
                    />
                  </Grid.Col>
                </Grid>
              )}
            </>
          )}
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default FacebookGeneralSettings;
